export const DEFAULT_THEME_COLOR = '#4f46e5';
export const DEFAULT_SERVICE_LISTING_STYLE = 'cards';
export const SERVICE_LISTING_STYLE_OPTIONS = {
  CARDS: 'cards',
  COMPACT_ROWS: 'compactRows'
};
const HEX_COLOR_REGEX = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;

const clamp = (value) => Math.min(255, Math.max(0, Math.round(value)));

const normalizeHex = (value = '') => {
  const trimmed = value.trim();
  if (!HEX_COLOR_REGEX.test(trimmed)) {
    return DEFAULT_THEME_COLOR;
  }

  if (trimmed.length === 4) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toLowerCase();
  }

  return trimmed.toLowerCase();
};

const hexToRgb = (hex) => {
  const normalized = normalizeHex(hex);
  const value = normalized.slice(1);

  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16)
  };
};

const rgbToHex = ({ r, g, b }) => `#${[r, g, b]
  .map((channel) => clamp(channel).toString(16).padStart(2, '0'))
  .join('')}`;

const mixWithWhite = (hex, amount) => {
  const rgb = hexToRgb(hex);
  return rgbToHex({
    r: rgb.r + (255 - rgb.r) * amount,
    g: rgb.g + (255 - rgb.g) * amount,
    b: rgb.b + (255 - rgb.b) * amount
  });
};

const mixWithBlack = (hex, amount) => {
  const rgb = hexToRgb(hex);
  return rgbToHex({
    r: rgb.r * (1 - amount),
    g: rgb.g * (1 - amount),
    b: rgb.b * (1 - amount)
  });
};

const linearize = (channel) => {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance (sRGB), 0–1 */
const relativeLuminance = (rgb) => {
  const R = linearize(rgb.r);
  const G = linearize(rgb.g);
  const B = linearize(rgb.b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

/**
 * Darken toward black until the background is dark enough for white label text
 * (works when brand color is white or very light).
 */
const mixTowardBlackForWhiteText = (hex, maxLuminance = 0.24) => {
  let amount = 0.22;
  let mixed = mixWithBlack(hex, amount);
  let lum = relativeLuminance(hexToRgb(mixed));
  while (lum > maxLuminance && amount < 0.96) {
    amount += 0.05;
    mixed = mixWithBlack(hex, amount);
    lum = relativeLuminance(hexToRgb(mixed));
  }
  return mixed;
};

export const getLandingPageThemeColor = (landingPageSettings = {}) => (
  landingPageSettings.themeColor ||
  landingPageSettings.headerTextColor ||
  landingPageSettings.primaryColor ||
  DEFAULT_THEME_COLOR
);

export const getLandingPageServiceListingStyle = (landingPageSettings = {}) => {
  const value = landingPageSettings.serviceListingStyle;

  if (value === SERVICE_LISTING_STYLE_OPTIONS.COMPACT_ROWS) {
    return SERVICE_LISTING_STYLE_OPTIONS.COMPACT_ROWS;
  }

  return DEFAULT_SERVICE_LISTING_STYLE;
};

export const getLandingPageCustomStyles = (landingPageSettings = {}) => {
  const themeColor = normalizeHex(getLandingPageThemeColor(landingPageSettings));
  const themeRgb = hexToRgb(themeColor);
  const { r, g, b } = themeRgb;
  const themeLum = relativeLuminance(themeRgb);
  const headerTextOnWhite = themeLum > 0.5 ? '#0f172a' : themeColor;
  const headerCtaBg = mixTowardBlackForWhiteText(themeColor);
  const storefrontBackgroundImageUrl = typeof landingPageSettings.storefrontBackgroundImageUrl === 'string'
    ? landingPageSettings.storefrontBackgroundImageUrl.trim()
    : '';
  const storefrontBackgroundOverlayOpacity = Number.isFinite(Number(landingPageSettings.storefrontBackgroundOverlayOpacity))
    ? Math.min(0.9, Math.max(0, Number(landingPageSettings.storefrontBackgroundOverlayOpacity)))
    : 0.1;

  const dtPrimaryDark = mixWithBlack(themeColor, 0.22);

  return {
    '--page-accent': themeColor,
    '--page-accent-strong': mixWithBlack(themeColor, 0.18),
    '--page-accent-soft': mixWithWhite(themeColor, 0.9),
    '--page-accent-rgb': `${r}, ${g}, ${b}`,
    '--dt-primary': themeColor,
    '--dt-primary-dark': dtPrimaryDark,
    '--header-text-color': headerTextOnWhite,
    '--header-cta-bg': headerCtaBg,
    '--header-cta-fg': '#ffffff',
    '--page-background-overlay-opacity': storefrontBackgroundOverlayOpacity,
    '--page-background-image': storefrontBackgroundImageUrl
      ? `url("${storefrontBackgroundImageUrl.replace(/"/g, '\\"')}")`
      : 'none'
  };
};
